import React from 'react'
import { connect } from 'react-redux'

import { RootStateTypeExtra } from '@store/types'

import { PrecinctList } from '@components/compare-property/area-list'
import ComparablePropertyItemHeader from '@components/compare-property/comparable-property-item/comparable-property-item-header'
import { LotsList } from '@components/compare-property/properties-list'
import { AreaListSkeleton } from '@components/compare-property/skeleton'
import DataHandler, { Status } from '@components/data-handler/data-handler'

import {
  PrecinctListItemInterface,
  selectPrecinctListFromResult,
  useGetPrecinctListQuery,
} from '@api/houseAndLand'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { LotProperty } from '@adUtilities/types'

interface LotSelectorProps {
  projectId: string
  lot?: LotProperty
  lotIndex?: number
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  onSelectPrecinct?: (id: string) => void
  activePrecinct?: string
  onToggleList?: (state: boolean) => void
}

function LotSelector({
  projectId,
  lot,
  lotIndex,
  comparePropertyProps,
  onSelectPrecinct,
  activePrecinct,
  onToggleList,
}: LotSelectorProps) {
  const [showPrecinctList, setShowPrecinctList] = React.useState(false)
  const [showPropertiesList, setShowPropertiesList] = React.useState(
    lotIndex === undefined
  )

  const precinctsQueryResponse = useGetPrecinctListQuery(
    { projectName: projectId },
    {
      selectFromResult: selectPrecinctListFromResult,
    }
  )

  const precincts = React.useMemo<PrecinctListItemInterface[]>(() => {
    if (precinctsQueryResponse.status !== Status.FULFILLED) {
      return []
    }

    return precinctsQueryResponse.precinctList
  }, [precinctsQueryResponse.status])

  const getSelectedPrecinct = React.useCallback(() => {
    if (lotIndex === undefined) {
      return activePrecinct
    }
    return lot?.references?.precinctId
  }, [activePrecinct, lotIndex, lot?.references?.precinctId])

  const [selectedPrecinct, setSelectedPrecinct] = React.useState(
    getSelectedPrecinct()
  )

  const resetSelectedPrecinct = React.useCallback(() => {
    const precinct = getSelectedPrecinct()
    if (precinct) {
      setSelectedPrecinct(precinct)
    }
  }, [getSelectedPrecinct])

  const handleTogglePrecinctList = React.useCallback(() => {
    setShowPrecinctList((prevVal) => {
      if (prevVal && lotIndex === undefined) {
        return prevVal
      }
      resetSelectedPrecinct()
      return !prevVal
    })
    setShowPropertiesList(false)
  }, [lotIndex, resetSelectedPrecinct])

  const handleSelectPrecinct = React.useCallback(
    (id: string) => {
      setSelectedPrecinct(id)
      setShowPrecinctList(false)
      onSelectPrecinct?.(id)
      setShowPropertiesList(true)
    },
    [onSelectPrecinct]
  )

  const handleTogglePropertiesList = React.useCallback(() => {
    setShowPropertiesList((prevVal) => {
      if (prevVal && lotIndex === undefined) {
        return prevVal
      }
      resetSelectedPrecinct()
      return !prevVal
    })
    setShowPrecinctList(false)
  }, [lotIndex, resetSelectedPrecinct])

  React.useEffect(() => {
    onToggleList?.(showPrecinctList || showPropertiesList)
  }, [showPrecinctList, showPropertiesList, onToggleList])

  return (
    <>
      <ComparablePropertyItemHeader
        propertyName={lot?.data?.name}
        areaChangerIsActive={showPrecinctList}
        onAreaChangerActionClick={handleTogglePrecinctList}
        onPropertyChangerActionClick={handleTogglePropertiesList}
        propertyChangerIsActive={showPropertiesList}
        labelPrefix="Lot "
        highlightAreaChanger={showPrecinctList}
        highlightPropertyChanger={showPropertiesList}
        hideAreaChangerAction={precincts.length < 2}
      />
      {showPrecinctList ? (
        <PrecinctList
          className="!h-page-body"
          onSelect={handleSelectPrecinct}
          selectedPrecinct={selectedPrecinct}
          precincts={precincts}
        />
      ) : null}
      {showPropertiesList && lotIndex !== undefined ? (
        <DataHandler
          payloads={{
            ...precinctsQueryResponse,
            data: precinctsQueryResponse.precinctList,
          }}
          skeletonFrame={<AreaListSkeleton />}
        >
          <LotsList
            className="!h-page-body"
            comparePropertyProps={comparePropertyProps}
            actionMode="replace"
            replaceIndex={lotIndex}
            onActionComplete={handleTogglePropertiesList}
            selectedPrecinct={selectedPrecinct}
          />
        </DataHandler>
      ) : null}
      {showPropertiesList && lotIndex === undefined ? (
        <LotsList
          className="!h-page-body"
          comparePropertyProps={comparePropertyProps}
          actionMode="add"
          onActionComplete={handleTogglePropertiesList}
          selectedPrecinct={selectedPrecinct}
        />
      ) : null}
    </>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectId: projectIdentity.projectId,
    activePrecinct: session?.houseAndLand?.activePrecinctId,
  })
)(LotSelector)
